import React, { useState } from 'react';
import {
  TableRow, TableCell, RowViewButtons, RowEditButtons,
  DataTableUrlProps, setSorterInUrl, DataTable,
} from 'ui/components/DataTable';
import { TextInput, Text } from '@patternfly/react-core';
import { useDispatch, useSelector } from 'react-redux';
import { Sorter } from 'types';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { tenantSelectors } from 'store/tenant';
import { useValidators } from 'util/ValidationUtils';
import { getPropsFromUrl } from 'util/BookmarkableUtils';
import { usePageableData } from 'util/FunctionalComponentUtils';
import { holidayOperations, holidaySelectors } from 'store/holiday';
import { Holiday } from 'domain/Holiday';
import LoadingSpinner from 'ui/components/LoadingSpinner';
/* eslint-disable */

export type Props = RouteComponentProps;

export function HolidayRow({ holiday, setIsLoading } : { holiday: Holiday; setIsLoading: (loading: boolean) => void }) {
  const [isEditing, setIsEditing] = useState(false);
  const dispatch = useDispatch();
  const { t } = useTranslation('HolidaysPage');

  if (isEditing) {
    return (<EditableHolidayRow holiday={holiday} isNew={false} onClose={() => setIsEditing(false)} setIsLoading={setIsLoading}  />);
  }

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      await dispatch(holidayOperations.removeHoliday(holiday))
      await dispatch(holidayOperations.refreshHolidayList());
    } catch (error) {
      console.error("Error al eliminar el día festivo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <TableRow>
      <TableCell columnName={t('dateHoliday')}>
        <Text>{holiday.date}</Text>
      </TableCell>
      <TableCell columnName={t('descriptionHoliday')}>
        <Text>{holiday.description}</Text>
      </TableCell>
      <RowViewButtons
        onEdit={() => setIsEditing(true)}
        onDelete={handleDelete}
      />
    </TableRow>
  );
}

export function EditableHolidayRow(props: { holiday: Holiday; isNew: boolean; onClose: () => void; setIsLoading: (loading: boolean) => void; }) {
  const [date, setDate] = useState(props.holiday.date);
  const [description, setDescription] = useState(props.holiday.description);

  const dispatch = useDispatch();
  const holidayList = useSelector(holidaySelectors.getHolidayList);
  const { t } = useTranslation('HolidaysPage');

  const validators = {
    dateMustNotBeEmpty: {
      predicate: (h: Holiday) => !!h.date,
      errorMsg: () => t('holidayEmptyDateError'),
    },
    dateAlreadyTaken: {
      predicate: (h: Holiday) => {
        if (!h.date) return true; // No hay fecha, se validará por otro validador
        return !holidayList.some(other => 
          other.id !== h.id &&
          new Date(other.date).toISOString().split('T')[0] === new Date(h.date).toISOString().split('T')[0]
        );
      },
      errorMsg: () => t('holidayDateAlreadyTakenError'),
    },
    dateMustNotBeInPast: {
      predicate: (h: Holiday) => {
        if (!h.date) return true; // Será atrapado por otro validador
        const todayStr = new Date().toISOString().split('T')[0];
        const inputDateStr = new Date(h.date).toISOString().split('T')[0];
        return inputDateStr >= todayStr;
      },
      errorMsg: () => t('dateMustNotBeInPast'),
    },
  };

  const updatedHoliday: Holiday = {
    ...props.holiday,
    date,
    description,
  };

  const validationErrors = useValidators(updatedHoliday, validators);

  const handleSave = async () => {
    props.setIsLoading(true);
    try {
      if (props.isNew) {
        await dispatch(holidayOperations.addHoliday(updatedHoliday));
      } else {
        await dispatch(holidayOperations.updateHoliday(updatedHoliday));
      }
      await dispatch(holidayOperations.refreshHolidayList());
    } catch (error) {
      console.error('Error al guardar el día festivo:', error);
    } finally {
      props.setIsLoading(false);
    }
  };


  return (
    <TableRow>
      <TableCell columnName={t('dateHoliday')}>
        <TextInput 
          type='date'
          value={new Date(date).toISOString().split('T')[0]}
          onChange={(value) => setDate(new Date(value))} 
        />
        {validationErrors.showValidationErrors('dateMustNotBeEmpty', 'dateAlreadyTaken', 'dateMustNotBeInPast')}
      </TableCell>
      <TableCell columnName={t('maxMinutesPerDay')}>
        <TextInput
          value={description || ''}
          onChange={(value) => {
            setDescription(value);
          }}
          type="text"
        />
      </TableCell>
      <RowEditButtons
        isValid={validationErrors.isValid}
        onSave={handleSave}
        onClose={() => props.onClose()}
      />
    </TableRow>
  );
}

export const HolidaysPage: React.FC<Props> = (props) => {
  const holidayList = useSelector(holidaySelectors.getHolidayList);
  const tenantId = useSelector(tenantSelectors.getTenantId);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation('HolidaysPage');

  const columns = [
    { name: t('dateHoliday'), sorter: (a: Holiday, b: Holiday) => a.date.toString().localeCompare(b.date.toString())},
    { name: t('descriptionHoliday') },
  ];

  const urlProps = getPropsFromUrl<DataTableUrlProps>(props, {
    page: '1',
    itemsPerPage: '10',
    filter: null,
    sortBy: '0',
    asc: 'true',
  });

  const sortBy = parseInt(urlProps.sortBy || '-1', 10);
  const sorter = columns[sortBy].sorter as Sorter<Holiday>;

  const pageableData = usePageableData(
    urlProps,
    holidayList,
    (holiday) => [
      new Date(holiday.date).toISOString().split('T')[0],
      `${holiday.description || ''}`],
    sorter,
  );

  return (
    <>
      <LoadingSpinner isLoading={isLoading} />
      <DataTable
        {...props}
        {...pageableData}
        title={t('Holidays')}
        columns={columns}
        rowWrapper={(holiday) => (<HolidayRow key={holiday.id} holiday={holiday} setIsLoading={setIsLoading} />)}
        sortByIndex={sortBy}
        onSorterChange={(index) => setSorterInUrl(props, urlProps, sortBy, index)}
        newRowWrapper={(removeRow) => (
          <EditableHolidayRow
            isNew
            onClose={removeRow}
            holiday={{
              tenantId,
              date: new Date(),
              description: null,
            }}
            setIsLoading={setIsLoading}
          />
        )}
      />
    </>
  );
};

export default withRouter(HolidaysPage);
